<template>
  <div class="salon-app salon-login">
    <!-- Top app bar + booking progress (same shell as SH001) -->
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-back" type="button" :aria-label="$t('salon.common.goBack')" @click="goBack">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.flow.salonBooking") }}</h1>
      </div>
      <div class="salon-progress">
        <div class="salon-progress-track">
          <div class="salon-progress-fill" :style="{ width: progressWidth }"></div>
        </div>
        <!-- SH002 shows the chosen outlet beside the step count (Salon_UI/phone_number_*),
             where SH001 only had room for the step count. -->
        <div class="salon-progress-meta">
          <span class="salon-progress-outlet">{{ outletName }}</span>
          <span class="salon-progress-label">{{ $t("salon.flow.stepOf", { step, total: totalSteps }) }}</span>
        </div>
      </div>
    </header>

    <main class="salon-main">
      <section class="salon-intro">
        <h2 class="salon-intro-title">{{ $t("salon.login.title") }}</h2>
        <p class="salon-intro-subtitle">
          {{ $t("salon.login.subtitle") }}
        </p>
      </section>

      <!-- ── Mobile number ─────────────────────────────────────── -->
      <div class="salon-field">
        <label class="salon-field-label" for="salon_phone_number">{{ $t("salon.login.phoneLabel") }}</label>
        <div class="salon-phone" :class="{ 'salon-phone--active': hasPhone }">
          <span class="salon-phone-flag" aria-hidden="true">&#127480;&#127468;</span>
          <span class="salon-phone-code">+{{ phoneCode }}</span>
          <span class="salon-phone-divider" aria-hidden="true"></span>
          <input
            id="salon_phone_number"
            ref="phoneInput"
            class="salon-phone-input"
            type="tel"
            inputmode="numeric"
            autocomplete="tel-national"
            name="salon_phone_number"
            placeholder="9123 4567"
            :value="phoneDisplay"
            @input="onPhoneInput"
            @keyup.enter="submit"
          />
        </div>
        <p class="salon-field-hint">{{ $t("salon.login.phoneHint") }}</p>
      </div>

      <p v-if="errorMessage" class="salon-error" role="alert">{{ errorMessage }}</p>

      <!-- ── Reminders opt-in ──────────────────────────────────── -->
      <label class="salon-check">
        <input v-model="wantsReminders" class="salon-check-input" type="checkbox" name="salon_reminders" />
        <span class="salon-check-box" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2.5"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <span class="salon-check-label">{{ $t("salon.login.reminders") }}</span>
      </label>
    </main>

    <nav class="salon-cta">
      <button class="salon-cta-btn" type="button" :disabled="!canContinue || isChecking" @click="submit">
        <span>{{ $t("salon.flow.continue") }}</span>
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M5 12h14m-6-6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </nav>

    <!-- ── "Checking your profile" overlay (Salon_UI/profile_check_overlay) ── -->
    <div v-if="isChecking" class="salon-overlay">
      <div class="salon-overlay-card" role="status" aria-live="polite">
        <svg class="salon-spinner" viewBox="0 0 50 50" aria-hidden="true">
          <circle class="salon-spinner-path" cx="25" cy="25" r="20" fill="none" stroke="currentColor"
            stroke-width="4" stroke-linecap="round" stroke-dasharray="90 150" />
        </svg>
        <h3 class="salon-overlay-title">{{ $t("salon.login.checking") }}</h3>
        <p class="salon-overlay-phone">{{ formattedPhone }}</p>
      </div>
    </div>

    <!-- ── PIN dialog ────────────────────────────────────────────
         Only a number that already has an account gets here; a number that does not goes
         straight to registration, so the PIN is never asked of someone who has not set one. -->
    <div
      v-if="showPinDialog"
      class="salon-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="salon_pin_title"
    >
      <div class="salon-overlay-card salon-pin-card">
        <button class="salon-pin-close" type="button" :aria-label="$t('salon.login.close')" @click="closePinDialog">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" />
          </svg>
        </button>

        <h3 id="salon_pin_title" class="salon-overlay-title">{{ $t("salon.login.pinTitle") }}</h3>
        <p class="salon-overlay-phone">{{ formattedPhone }}</p>

        <SalonPinInput
          ref="pinInput"
          class="salon-pin-field"
          :model-value="pin"
          :length="pinLength"
          name="salon_pin"
          label="PIN"
          labelledby="salon_pin_title"
          @update:model-value="onPinChanged"
        />

        <p v-if="isVerifying" class="salon-pin-status" role="status">{{ $t("salon.login.signingIn") }}</p>
        <p v-else-if="pinError" class="salon-error salon-pin-status" role="alert">{{ pinError }}</p>
        <p v-else class="salon-pin-status">{{ $t("salon.login.pinHint", { length: pinLength }) }}</p>

        <button class="salon-link" type="button" @click="goToForgotPin">{{ $t("salon.login.forgotPin") }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import SalonPinInput from "@/salon/components/PinInput.vue";
import MemberAuthService from "@/services/memberAuth.service";
import MemberService from "@/services/member.service";
import { localStorageName } from "@/config/utils";
import "@/salon/styles/tokens.css";

// SH002 is the second of the nine salon booking steps (Salon_UI/phone_number_*,
// profile_check_overlay). The page itself only asks for the number: Continue looks the
// number up, and where it goes next depends on the answer — a number with no account
// goes on to registration (SH003), a number with one is asked for its PIN in a dialog.
const STEP = 2;
const TOTAL_STEPS = 9;
const PHONE_CODE = "65";
const PHONE_LENGTH = 8;
const PIN_LENGTH = 4;

export default {
  name: "SalonLogin",
  components: {
    SalonPinInput,
  },
  emits: ["back", "logged-in", "register"],
  data() {
    return {
      step: STEP,
      totalSteps: TOTAL_STEPS,
      phoneCode: PHONE_CODE,
      pinLength: PIN_LENGTH,
      phoneDigits: "",
      pin: "",
      wantsReminders: false,
      isChecking: false,
      isVerifying: false,
      showPinDialog: false,
      errorMessage: "",
      pinError: "",
    };
  },
  computed: {
    progressWidth() {
      return ((STEP / TOTAL_STEPS) * 100).toFixed(2) + "%";
    },
    // App.vue writes branch_info from /check-merchant-and-branch before this view
    // mounts, so the outlet chosen in SH001 can be named without another call.
    outletName() {
      const branch = JSON.parse(localStorage.getItem(localStorageName("BRANCH_INFO")) || "{}");
      return branch.branch_name || "";
    },
    hasPhone() {
      return this.phoneDigits.length > 0;
    },
    // "9123 4567" — grouped for reading, but never sent to the API this way.
    phoneDisplay() {
      if (this.phoneDigits.length <= 4) {
        return this.phoneDigits;
      }
      return this.phoneDigits.slice(0, 4) + " " + this.phoneDigits.slice(4);
    },
    formattedPhone() {
      return "+" + PHONE_CODE + " " + this.phoneDisplay;
    },
    canContinue() {
      return this.phoneDigits.length === PHONE_LENGTH;
    },
  },
  methods: {
    onPhoneInput(event) {
      this.errorMessage = "";
      this.phoneDigits = event.target.value.replace(/\D/g, "").slice(0, PHONE_LENGTH);
      // The field is :value-bound, so a rejected keystroke (a letter, a 9th digit)
      // leaves the DOM ahead of the model until it is written back by hand.
      this.$nextTick(() => {
        if (this.$refs.phoneInput) {
          this.$refs.phoneInput.value = this.phoneDisplay;
        }
      });
    },

    // Bound to the component's event rather than a watcher on `pin`: a watcher also
    // fires for clearPin()'s own reset, which wiped the "wrong PIN" message a moment
    // after verifyPin() set it, so the error never appeared.
    onPinChanged(value) {
      this.pin = value;
      this.pinError = "";

      // The dialog has no button of its own — the fourth digit is the submit.
      if (value.length === PIN_LENGTH) {
        this.verifyPin();
      }
    },

    openPinDialog() {
      this.pin = "";
      this.pinError = "";
      this.showPinDialog = true;
      this.$nextTick(() => this.focusPin());
    },

    closePinDialog() {
      this.showPinDialog = false;
      this.pin = "";
      this.pinError = "";
    },

    focusPin() {
      if (this.$refs.pinInput) {
        this.$refs.pinInput.focusFirst();
      }
    },

    clearPin() {
      this.pin = "";
      this.$nextTick(() => this.focusPin());
    },

    // apiMinty stamps MerchantId onto every request from localStorage, so it has to be
    // there before the first member-auth call — the same guard SignIn.validatePhone uses.
    async ensureMerchantId() {
      const merchantCode = this.$route.params.merchant_code || "";

      if (localStorage.getItem(localStorageName("MERCHANT_ID")) || !merchantCode) {
        return;
      }

      const response = await MemberService.getMerchantId({ merchant_code: merchantCode });

      if (response.data.result.isSuccess) {
        localStorage.setItem(localStorageName("MERCHANT_ID"), response.data.data.merchantId);
      }
    },

    // Step one: look the number up and decide where it goes.
    async submit() {
      if (!this.canContinue || this.isChecking) {
        return;
      }

      this.errorMessage = "";
      this.isChecking = true;

      try {
        await this.ensureMerchantId();

        const validation = await MemberAuthService.validatePhoneNumber(this.phoneDigits, PHONE_CODE);

        if (!validation.data.result.isSuccess) {
          this.errorMessage = validation.data.result.message;
          return;
        }

        // Anything without a PIN goes to registration, not to the PIN dialog. That covers
        // a number nobody has ever used and one the POS created for a walk-in — both are
        // profiles the customer still has to finish, and member/salon/register completes
        // an existing row in place rather than refusing it.
        if (validation.data.data.is_empty_passcode) {
          this.$emit("register", { phone_number: this.phoneDigits, phone_code: PHONE_CODE });
          return;
        }

        this.openPinDialog();
      } catch (error) {
        this.errorMessage = this.$t("salon.login.networkError");
      } finally {
        this.isChecking = false;
      }
    },

    // Step two: the fourth PIN digit calls this on its own — the dialog has no button.
    async verifyPin() {
      if (this.isVerifying) {
        return;
      }

      this.pinError = "";
      this.isVerifying = true;

      try {
        const data = await this.$store.dispatch("auth/loginOtp", {
          phone_number: this.phoneDigits,
          phone_code: PHONE_CODE,
          passcode: this.pin,
          otp: "",
          i_agree: true,
          is_empty_passcode: false,
        });

        if (!data || !data.result.isSuccess) {
          this.pinError = (data && data.result.message) || this.$t("salon.login.pinMismatch");
          this.clearPin();
          return;
        }

        this.showPinDialog = false;

        this.$emit("logged-in", {
          user: data.data.user,
          phone_number: this.phoneDigits,
          phone_code: PHONE_CODE,
          wants_reminders: this.wantsReminders,
        });
      } catch (error) {
        this.pinError = this.$t("salon.login.networkError");
        this.clearPin();
      } finally {
        this.isVerifying = false;
      }
    },

    goToForgotPin() {
      this.$router.push({ name: "ForgotPasscode", params: { ...this.$route.params } });
    },

    // The salon steps share one URL — the step lives in BookingFlow's state, not in
    // history — so $router.back() here would leave the flow altogether.
    goBack() {
      this.$emit("back");
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-login {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 8px;
  background: var(--salon-surface);
  border-bottom: 2px solid var(--salon-surface-variant);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 0;
  background: transparent;
  color: var(--salon-primary);
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

.salon-progress {
  margin-top: 16px;
}

.salon-progress-track {
  width: 100%;
  height: 3px;
  overflow: hidden;
  background: var(--salon-surface-container-highest);
  border-radius: 999px;
}

.salon-progress-fill {
  height: 100%;
  background: var(--salon-primary-container);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.salon-progress-meta {
  display: flex;
  gap: 12px;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 8px;
}

.salon-progress-outlet {
  overflow: hidden;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.salon-progress-label {
  flex-shrink: 0;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-lg);
  padding: var(--salon-stack-lg) var(--salon-container-padding) 140px;
}

.salon-intro {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-sm);
}

.salon-intro-title {
  margin: 0;
  font-family: var(--salon-font-headline);
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  letter-spacing: -0.01em;
  color: var(--salon-on-background);
}

.salon-intro-subtitle {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── Fields ────────────────────────────────────────────────────── */
.salon-field {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-sm);
}

.salon-field-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-on-surface);
}

.salon-field-hint {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-link {
  align-self: flex-start;
  padding: 0;
  font-family: var(--salon-font-body);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: var(--salon-on-surface);
  text-decoration: underline;
  text-underline-offset: 3px;
  background: none;
  border: 0;
  cursor: pointer;
}

.salon-error {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--salon-error);
}

/* ── Phone ─────────────────────────────────────────────────────── */
.salon-phone {
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 16px;
  overflow: hidden;
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-border);
  border-radius: var(--salon-radius-input);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.salon-phone--active,
.salon-phone:focus-within {
  border-color: var(--salon-primary-container);
  box-shadow: 0 0 0 1px var(--salon-primary-container);
}

.salon-phone-flag {
  font-size: 20px;
  line-height: 1;
}

.salon-phone-code {
  margin-left: 8px;
  font-size: 16px;
  line-height: 24px;
  color: var(--salon-on-surface);
}

.salon-phone-divider {
  width: 1px;
  height: 32px;
  margin: 0 16px;
  background: var(--salon-surface-variant);
}

.salon-phone-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  font-family: var(--salon-font-body);
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.15em;
  color: var(--salon-on-surface);
  background: transparent;
  border: 0;
  outline: none;
  appearance: none;
}

.salon-phone-input::placeholder {
  color: var(--salon-surface-container-highest);
}

/* ── Reminders checkbox ────────────────────────────────────────── */
.salon-check {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  cursor: pointer;
}

.salon-check-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.salon-check-box {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  color: transparent;
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 4px;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.salon-check-input:checked + .salon-check-box {
  color: var(--salon-on-primary);
  background: var(--salon-primary-container);
  border-color: var(--salon-primary-container);
}

.salon-check-input:focus-visible + .salon-check-box {
  box-shadow: 0 0 0 2px var(--salon-secondary);
}

.salon-check-label {
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── Bottom CTA ────────────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  z-index: 50;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(16px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface);
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.04);
}

.salon-cta-btn {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.salon-cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.salon-cta-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.salon-cta-btn:disabled {
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-variant);
  cursor: not-allowed;
  opacity: 0.7;
}

/* ── "Checking your profile" overlay ───────────────────────────── */
.salon-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
}

.salon-overlay-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 280px;
  padding: 24px;
  text-align: center;
  background: var(--salon-surface-lowest);
  border-radius: var(--salon-radius-card);
  box-shadow: 0 4px 20px rgba(28, 28, 30, 0.08);
}

.salon-spinner {
  width: 40px;
  height: 40px;
  margin-bottom: 20px;
  color: var(--salon-secondary-container);
  animation: salon-spin 2s linear infinite;
}

.salon-spinner-path {
  animation: salon-dash 1.5s ease-in-out infinite;
}

.salon-overlay-title {
  margin: 0 0 4px;
  /* material-dashboard forces Roboto Slab onto every h1-h3; the overlay title is
     Inter in the reference (Salon_UI/profile_check_overlay), so say so explicitly. */
  font-family: var(--salon-font-body);
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-overlay-phone {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: var(--salon-on-surface-variant);
}

/* ── PIN dialog ────────────────────────────────────────────────── */
.salon-pin-card {
  position: relative;
  /* Four 56px boxes plus their gaps need more room than the spinner card. */
  max-width: 340px;
  padding: 28px 20px 20px;
}

.salon-pin-close {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  padding: 6px;
  color: var(--salon-on-surface-variant);
  background: none;
  border: 0;
  cursor: pointer;
}

.salon-pin-field {
  margin-top: 20px;
}

/* The shared .salon-link is left-aligned for the page; inside the centred card it has
   to follow the card. */
.salon-pin-card .salon-link {
  align-self: center;
}

.salon-pin-status {
  min-height: 32px;
  margin: 12px 0 4px;
  font-size: 12px;
  line-height: 16px;
  color: var(--salon-on-surface-variant);
}

@keyframes salon-spin {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes salon-dash {
  0% {
    stroke-dasharray: 1 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90 150;
    stroke-dashoffset: -124;
  }
}

@media (prefers-reduced-motion: reduce) {
  .salon-spinner,
  .salon-spinner-path {
    animation: none;
  }
}
</style>
